/**
 * User Details Drawer — Premium redesign (USERS-UI-001)
 * Tabs: Profile (default) | Access | Activity | Auto Assignment (processors only)
 * Footer: admin actions only (Reset Password | Lock/Unlock | Impersonate). Header X closes.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { getUserRoles, updateUserQCRequirement, getUserActivityEvents } from '../utils/usersApi';
import { listAuditLogs } from '../utils/auditApi';
import { getUserStatus } from '../utils/userStatus';
import AutoAssignmentSettings from './admin/AutoAssignmentSettings';
import { toast } from '../utils/toast';
import {
  Mail, KeyRound, Lock, Unlock, Users, Copy, ChevronRight,
  ShieldCheck, LogIn, LogOut, Plus, Pencil, Trash2, UserCog, Pin,
  CheckCircle2, XCircle, FileText, Clock, X, UserRound, Code2, Activity, Fingerprint
} from 'lucide-react';
import './UserDetailsDrawer.css';
import './UserDetailsDrawer.premium.css';
import { formatDateTimeET } from '../utils/formatters';

const ACTION_ICONS = [
  ['login', LogIn], ['logout', LogOut], ['create', Plus], ['update', Pencil],
  ['delete', Trash2], ['lock', Lock], ['unlock', Unlock], ['reset_password', KeyRound],
  ['password', KeyRound], ['impersonat', UserCog], ['assign', Pin],
  ['complete', CheckCircle2], ['cancel', XCircle],
];

const ActionIcon = ({ action }) => {
  const a = (action || '').toLowerCase();
  const match = ACTION_ICONS.find(([key]) => a === key || a.includes(key));
  const Icon = match ? match[1] : FileText;
  return <Icon className="w-4 h-4" />;
};

const UserDetailsDrawer = ({
  user,
  isOpen,
  onClose,
  onResetPassword,
  onResendVerification,
  onLockUnlock,
  onImpersonate,
  onUserUpdated,
  onEdit = null,
  currentUserId = null,
  impersonationActive = false
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('profile');
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showTechDetails, setShowTechDetails] = useState(true);
  // USERS-UI-002: keep mounted for the close transition; animated tab underline
  const [rendered, setRendered] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const tabsRef = useRef(null);
  const [ink, setInk] = useState({ left: 0, width: 0 });
  const [loading, setLoading] = useState(false);
  const [resendingVerification] = useState(false);

  // Activity tab state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);

  // QC toggle state
  const [qcToggleLoading, setQcToggleLoading] = useState(false);
  const [localRequiresQc, setLocalRequiresQc] = useState(user?.requires_qc !== false);

  useEffect(() => {
    setLocalRequiresQc(user?.requires_qc !== false);
  }, [user?.requires_qc, user?.id]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('profile');
      setShowTechDetails(true);
      setShowPermissions(false);
      setActivityPage(1);
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (isOpen) { setClosing(false); setRendered(true); return undefined; }
    if (!rendered) return undefined;
    setClosing(true);
    const t = setTimeout(() => { setRendered(false); setClosing(false); }, 200);
    return () => clearTimeout(t);
  }, [isOpen, rendered]);

  useLayoutEffect(() => {
    const el = tabsRef.current?.querySelector('.tab-button.active');
    if (el) setInk({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeTab, rendered, user?.id]);

  const canViewRbac = hasPermission('rbac', 'view') || hasPermission('users', 'view_roles');
  const canResetPassword = hasPermission('users', 'reset_password');
  const canLock = hasPermission('users', 'lock');
  const canImpersonate = hasPermission('users', 'impersonate');
  const canViewAudit = hasPermission('audit', 'view');
  const canManageAutoAssign = hasPermission('users', 'update');
  const canEditUser = hasPermission('users', 'update');

  const isSelf = currentUserId && user?.id === currentUserId;
  const statusInfo = getUserStatus(user || {});
  const isLocked = statusInfo.isLocked;
  const isPendingVerification =
    user?.status?.toLowerCase() === 'pending_verification' ||
    user?.email_verified === false ||
    (user?.email_verified !== true && user?.status?.toLowerCase() !== 'active');

  const normalizeRoleStr = (r) => (r || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const isTaxProcessor = ['processor', 'tax_processor'].includes(normalizeRoleStr(user?.role)) ||
    (user?.roles && user.roles.some(r => r.toLowerCase().includes('processor') && !r.toLowerCase().includes('search'))) ||
    (userRoles && userRoles.some(r => r.name?.toLowerCase().includes('processor') && !r.name?.toLowerCase().includes('search')));
  const isSearchProcessor = normalizeRoleStr(user?.role) === 'search_processor' ||
    (user?.roles && user.roles.some(r => normalizeRoleStr(r) === 'search_processor')) ||
    (userRoles && userRoles.some(r => normalizeRoleStr(r?.name) === 'search_processor'));
  const isAutoAssignableProcessor = isTaxProcessor || isSearchProcessor;

  const isProcessorRole = (role) => (role || '').toLowerCase().includes('processor');
  const isUserProcessor = user?.roles?.some(r => isProcessorRole(r)) || isProcessorRole(user?.role);

  const handleDrawerQCToggle = async () => {
    if (!user || qcToggleLoading) return;
    const newValue = !localRequiresQc;
    const userName = user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email;
    setQcToggleLoading(true);
    setLocalRequiresQc(newValue);
    try {
      await updateUserQCRequirement(user.id, newValue);
      if (onUserUpdated) onUserUpdated(user.id, { requires_qc: newValue });
      if (activeTab === 'activity') setTimeout(() => fetchUserActivityLogs(), 500);
      toast.success(newValue
        ? `QC review enabled for ${userName}`
        : `QC review disabled for ${userName}`);
    } catch (error) {
      setLocalRequiresQc(!newValue);
      toast.error(`Failed to update QC requirement: ${error.message || 'Unknown error'}`);
    } finally {
      setQcToggleLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user && canViewRbac) fetchUserRoles();
    if (isOpen && user && canViewAudit && activeTab === 'activity') fetchUserActivityLogs();
  }, [isOpen, user?.id, canViewRbac, canViewAudit, activeTab, activityPage]);

  const fetchUserRoles = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const rolesData = await getUserRoles(user.id);
      const normalizedRoles = (rolesData.roles || []).map(r => (typeof r === 'string' ? { name: r } : r));
      setUserRoles(normalizedRoles);
      const allPerms = [];
      normalizedRoles.forEach(role => {
        (role.permissions || []).forEach(perm => {
          const key = `${perm.resource}:${perm.action}`;
          if (!allPerms.find(p => `${p.resource}:${p.action}` === key)) allPerms.push(perm);
        });
      });
      setUserPermissions(allPerms);
    } catch (err) {
      console.error('Error fetching user roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivityLogs = async () => {
    if (!user) return;
    try {
      setActivityLoading(true);
      try {
        const activityData = await getUserActivityEvents(user.id, { page: activityPage, page_size: 10 });
        if (activityData.logs && activityData.logs.length > 0) {
          setActivityLogs(activityData.logs);
          setActivityTotalPages(activityData.total_pages || 1);
          return;
        }
      } catch (activityErr) {
        // fall back to audit_logs below
      }
      const logsData = await listAuditLogs({ actor_id: user.id, page: activityPage, page_size: 10 });
      setActivityLogs(logsData.logs || []);
      setActivityTotalPages(logsData.total_pages || 1);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setActivityLogs([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return formatDateTimeET(dateString);
    } catch (error) {
      return dateString;
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  if (!rendered || !user) return null;

  const displayName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.email;
  const initials = (user.first_name && user.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`
    : (user.email || '?')[0]).toUpperCase();
  const fmtRole = (r) => r ? r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : null;
  const displayRole = (userRoles.length > 0 && userRoles[0].name)
    || fmtRole(user.roles?.[0] || user.role) || '—';

  // Group effective permissions by resource for compact display
  const groupedPermissions = userPermissions.reduce((acc, p) => {
    (acc[p.resource] = acc[p.resource] || []).push(p.action);
    return acc;
  }, {});

  return (
    <>
      <div className={`drawer-backdrop udd-backdrop${closing ? ' is-closing' : ''}`} onClick={onClose} data-testid="drawer-backdrop" />

      <div className={`user-details-drawer udd${closing ? ' is-closing' : ''}`} data-testid="user-details-drawer" data-state={closing ? 'closing' : 'open'} role="dialog" aria-modal="true" aria-label={`User details — ${displayName}`}>
        {/* Identity header */}
        <div className="drawer-header identity-header">
          <div className="identity-main">
            <div className="identity-avatar" data-testid="drawer-avatar">{initials}</div>
            <div className="identity-text">
              <div className="udd-name-row">
                <h2 data-testid="drawer-user-name">{displayName}</h2>
                <span className={`udd-status-pill status-badge ${statusInfo.badgeClass}`} data-testid={`drawer-status-header-${user.id}`}>{statusInfo.label}</span>
                {impersonationActive && <span className="impersonation-badge-small">Impersonating</span>}
              </div>
              <p className="identity-role" data-testid="drawer-user-role">{displayRole}</p>
              <span className="udd-email drawer-subtitle"><Mail className="w-3.5 h-3.5" />{user.email}</span>
            </div>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close" data-testid="drawer-close-x">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="drawer-tabs" ref={tabsRef} role="tablist">
          <span className="udd-tab-ink" style={{ width: ink.width, transform: `translateX(${ink.left}px)` }} aria-hidden="true" />
          <button
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
            data-testid="drawer-tab-profile" role="tab" aria-selected={activeTab === 'profile'}
          >
            <UserRound className="w-4 h-4" />Profile
          </button>
          {canViewRbac && (
            <button
              className={`tab-button ${activeTab === 'access' ? 'active' : ''}`}
              onClick={() => setActiveTab('access')}
              data-testid="drawer-tab-access" role="tab" aria-selected={activeTab === 'access'}
            >
              <ShieldCheck className="w-4 h-4" />Access
            </button>
          )}
          {canViewAudit && (
            <button
              className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
              data-testid="drawer-tab-activity" role="tab" aria-selected={activeTab === 'activity'}
            >
              <Activity className="w-4 h-4" />Activity
            </button>
          )}
          {canManageAutoAssign && isAutoAssignableProcessor && (
            <button
              className={`tab-button ${activeTab === 'auto-assign' ? 'active' : ''}`}
              onClick={() => setActiveTab('auto-assign')}
              data-testid="drawer-tab-auto-assign"
            >
              Auto Assignment
            </button>
          )}
        </div>

        <div className="drawer-content">
          <div className="udd-panel" key={activeTab} data-testid={`drawer-panel-${activeTab}`}>
          {activeTab === 'profile' && (
            <>
              {/* Lock alert — only when locked */}
              {isLocked && (
                <div className="lock-status-alert">
                  <div className="lock-status-header">
                    <Lock className="w-4 h-4 inline mr-1.5 -mt-0.5" /> <strong>Account Locked</strong>
                  </div>
                  <div className="lock-status-details">
                    {user.lock_reason && (
                      <div className="lock-detail-item">
                        <span className="lock-label">Reason:</span>
                        <span className="lock-value">{user.lock_reason}</span>
                      </div>
                    )}
                    {user.locked_until && (
                      <div className="lock-detail-item">
                        <span className="lock-label">Locked Until:</span>
                        <span className="lock-value">{formatDate(user.locked_until)}</span>
                      </div>
                    )}
                    {!user.lock_reason && (
                      <div className="lock-detail-item">
                        <span className="lock-reason">
                          {user.failed_login_attempts >= 3
                            ? 'Temporarily locked due to multiple failed sign-ins. Unlocks automatically at the time shown above.'
                            : 'This account was locked by an admin.'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Account card */}
              <section className="info-card" data-testid="profile-account-card">
                <div className="udd-sec-head">
                  <h3 className="udd-sec-title"><span className="udd-sec-icon"><UserRound className="w-4 h-4" /></span>Account Information</h3>
                  {onEdit && canEditUser && (
                    <button type="button" className="udd-sec-action" onClick={() => onEdit(user)} data-testid="drawer-edit-user-btn">
                      <Pencil className="w-3.5 h-3.5" />Edit
                    </button>
                  )}
                </div>
                <div className="card-rows">
                  <div className="card-row">
                    <span className="card-label">Full Name</span>
                    <span className="card-value"><span className="truncate">{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'Not set'}</span></span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Email</span>
                    <span className="card-value with-copy">
                      <span className="truncate">{user.email}</span>
                      <button className="btn-copy-icon" onClick={() => copyToClipboard(user.email, 'Email')} title="Copy email" data-testid="copy-email-btn">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Role</span>
                    <span className="card-value"><span className="udd-role-chip">{displayRole}</span></span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Status</span>
                    <span className="card-value">
                      <span className={`udd-status-pill status-badge ${statusInfo.badgeClass}`} data-testid={`drawer-status-basic-${user.id}`}>{statusInfo.label}</span>
                    </span>
                  </div>
                </div>
              </section>

              {/* Login & Security card */}
              <section className="info-card" data-testid="profile-security-card">
                <div className="udd-sec-head">
                  <h3 className="udd-sec-title"><span className="udd-sec-icon amber"><Lock className="w-4 h-4" /></span>Login &amp; Security</h3>
                </div>
                <div className="card-rows">
                  <div className="card-row">
                    <span className="card-label">Last Login (ET)</span>
                    <span className="card-value">{formatDate(user.last_login_at)}</span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">Created (ET)</span>
                    <span className="card-value">{formatDate(user.created_at)}</span>
                  </div>
                  <div className="card-row">
                    <span className="card-label">MFA</span>
                    <span className="card-value">
                      {user.mfa_enabled
                        ? <span className="udd-ok">Enabled</span>
                        : <span className="mfa-warning" data-testid="mfa-warning-badge">Not enabled</span>}
                    </span>
                  </div>
                  {user.locked_until && (
                    <div className="card-row">
                      <span className="card-label">Auto-Unlock (ET)</span>
                      <span className="card-value">{formatDate(user.locked_until)}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* QC Workflow (processors only) */}
              {isUserProcessor && (
                <section className="drawer-section qc-workflow-section">
                  <h3>QC Workflow Settings</h3>
                  <div className="qc-setting-row">
                    <div className="qc-setting-info">
                      <label>QC Review Required</label>
                      <p className="qc-setting-description">
                        {localRequiresQc
                          ? 'Orders completed by this user will be routed to QC review before delivery.'
                          : 'Orders completed by this user will be delivered directly without QC review.'}
                      </p>
                    </div>
                    <button
                      className={`qc-drawer-toggle ${localRequiresQc ? 'qc-enabled' : 'qc-disabled'} ${qcToggleLoading ? 'qc-loading' : ''}`}
                      onClick={handleDrawerQCToggle}
                      disabled={qcToggleLoading}
                      title="Click to toggle QC requirement"
                      data-testid="qc-drawer-toggle"
                    >
                      {qcToggleLoading ? (
                        <span className="qc-spinner">⟳</span>
                      ) : (
                        <>
                          <span className="qc-toggle-switch"><span className="qc-toggle-knob"></span></span>
                          <span className="qc-toggle-label">{localRequiresQc ? 'Enabled' : 'Disabled'}</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="qc-audit-note">Changes are recorded in the Activity tab.</p>
                </section>
              )}

              {/* Technical Details — collapsed by default */}
              <section className="tech-details" data-testid="technical-details-section">
                <button
                  type="button"
                  className="tech-details-toggle"
                  onClick={() => setShowTechDetails(v => !v)}
                  aria-expanded={showTechDetails}
                  data-testid="technical-details-toggle"
                >
                  <span className="udd-sec-icon slate"><Code2 className="w-4 h-4" /></span>
                  Technical Details
                  <ChevronRight className="w-4 h-4 udd-chev" />
                </button>
                {showTechDetails && (
                  <div className="tech-details-body" data-testid="technical-details-body">
                    <div className="card-row">
                      <span className="card-label">User ID</span>
                      <span className="card-value monospace with-copy">
                        <span className="truncate">{user.id}</span>
                        <button className="btn-copy-icon" onClick={() => copyToClipboard(user.id, 'User ID')} title="Copy ID" data-testid="copy-user-id-btn">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    </div>
                    <div className="card-row">
                      <span className="card-label">Failed Login Attempts</span>
                      <span className={`card-value ${user.failed_login_attempts >= 3 ? 'warning-text' : ''}`}>
                        {user.failed_login_attempts || 0}
                      </span>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}

          {activeTab === 'access' && canViewRbac && (
            <>
              <section className="info-card" data-testid="access-roles-card">
                <div className="udd-sec-head">
                  <h3 className="udd-sec-title"><span className="udd-sec-icon"><ShieldCheck className="w-4 h-4" /></span>Assigned Role{userRoles.length > 1 ? 's' : ''}</h3>
                </div>
                {loading ? (
                  <span className="text-muted">Loading...</span>
                ) : userRoles.length > 0 ? (
                  <div className="roles-list">
                    {userRoles.map((role, idx) => (
                      <div key={idx} className="role-card access-role-card">
                        <div className="role-icon"><ShieldCheck className="w-4 h-4" /></div>
                        <div>
                          <div className="role-name">{role.name}</div>
                          {role.description && <div className="role-description">{role.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="role-card access-role-card">
                    <div className="role-icon"><ShieldCheck className="w-4 h-4" /></div>
                    <div>
                      <div className="role-name">{displayRole}</div>
                      <div className="role-description">Legacy role assignment</div>
                    </div>
                  </div>
                )}
                <p className="access-note" data-testid="access-note">
                  This user's access is determined by the assigned role{userRoles.length > 1 ? 's' : ''} above.
                </p>
              </section>

              <section className="tech-details" data-testid="view-permissions-section">
                <button
                  type="button"
                  className="tech-details-toggle"
                  onClick={() => setShowPermissions(v => !v)}
                  aria-expanded={showPermissions}
                  data-testid="view-permissions-toggle"
                >
                  <span className="udd-sec-icon slate"><Fingerprint className="w-4 h-4" /></span>
                  View Permissions
                  {userPermissions.length > 0 && <span className="perm-count">{userPermissions.length}</span>}
                  <ChevronRight className="w-4 h-4 udd-chev" />
                </button>
                {showPermissions && (
                  <div className="tech-details-body" data-testid="permissions-body">
                    {loading ? (
                      <span className="text-muted">Loading...</span>
                    ) : userPermissions.length > 0 ? (
                      <div className="perm-groups">
                        {Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b)).map(([resource, actions]) => (
                          <div key={resource} className="perm-group">
                            <span className="perm-group-resource">{resource}</span>
                            <span className="perm-group-actions">{actions.sort().join(' · ')}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted" style={{ fontStyle: 'normal' }}>
                        Detailed permission entries are managed at the role level for this role.
                      </p>
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {activeTab === 'activity' && canViewAudit && (
            <section className="drawer-section">
              <div className="section-header">
                <h3>Recent Activity</h3>
                <button
                  className="btn-link"
                  onClick={() => {
                    const target = `/admin/audit-logs?actor_id=${user.id}`;
                    onClose();
                    setTimeout(() => navigate(target), 0);
                  }}
                  data-testid="view-all-activity-btn"
                >
                  View All Activity →
                </button>
              </div>

              {activityLoading ? (
                <div className="loading-state">Loading activity...</div>
              ) : activityLogs.length > 0 ? (
                <>
                  <div className="activity-timeline">
                    {activityLogs.map((log, idx) => (
                      <div key={log.id || idx} className="timeline-item">
                        <div className="timeline-icon lucide-icon">
                          <ActionIcon action={log.action} />
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="timeline-action">{log.action}</span>
                            <span className="timeline-time">{formatDate(log.timestamp || log.occurred_at)}</span>
                          </div>
                          {(log.resource || log.summary || log.entity_id) && (
                            <div className="timeline-details">
                              {(log.resource || log.summary) && (
                                <span className="timeline-resource">{log.resource || log.summary}</span>
                              )}
                              {log.entity_id && (
                                <span className="timeline-entity">• {log.entity_id.substring(0, 8)}</span>
                              )}
                            </div>
                          )}
                          {log.impersonator_name && (
                            <div className="timeline-impersonation">
                              Acting as: {log.impersonator_name}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {activityTotalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setActivityPage(p => Math.max(1, p - 1))}
                        disabled={activityPage === 1}
                        data-testid="activity-prev-btn"
                      >
                        Previous
                      </button>
                      <span className="page-info">Page {activityPage} of {activityTotalPages}</span>
                      <button
                        onClick={() => setActivityPage(p => Math.min(activityTotalPages, p + 1))}
                        disabled={activityPage === activityTotalPages}
                        data-testid="activity-next-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No recent activity for this user</p>
                </div>
              )}
            </section>
          )}

          {activeTab === 'auto-assign' && canManageAutoAssign && isAutoAssignableProcessor && (
            <AutoAssignmentSettings
              userId={user.id}
              userRole={user.role}
              userRoles={user.roles || userRoles}
              onUpdate={() => {}}
              onSaveComplete={onClose}
            />
          )}
          </div>
        </div>

        {/* Admin actions footer — security actions only, no Close */}
        {(!isSelf && (isPendingVerification || canResetPassword || canLock || canImpersonate)) && (
          <div className="drawer-footer admin-actions-footer" data-testid="drawer-admin-actions">
            <div className="footer-buttons">
              <div className="footer-group">
                {isPendingVerification && onResendVerification && (
                  <button type="button" className="udd-action ghost" onClick={() => onResendVerification(user)} disabled={resendingVerification}
                    title="Send verification email to this user" data-testid="resend-verification-btn">
                    <Mail className="w-4 h-4" />Resend Verification
                  </button>
                )}
                {canResetPassword && (
                  <button type="button" className="udd-action danger" onClick={() => onResetPassword(user)} data-testid="drawer-reset-password-btn">
                    <KeyRound className="w-4 h-4" />Reset Password
                  </button>
                )}
              </div>
              <div className="footer-group">
                {canLock && (
                  <button type="button" className={`udd-action ${isLocked ? 'success' : 'neutral'}`} onClick={() => onLockUnlock(user)} data-testid="drawer-lock-btn">
                    {isLocked ? <><Unlock className="w-4 h-4" />Unlock</> : <><Lock className="w-4 h-4" />Lock</>}
                  </button>
                )}
                {canImpersonate && !impersonationActive && (
                  <button type="button" className="udd-action warn" onClick={() => onImpersonate(user)} data-testid="drawer-impersonate-btn">
                    <Users className="w-4 h-4" />Impersonate
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserDetailsDrawer;
